import { useEffect, useMemo, useState } from "react";
import { STALE_AFTER_MS, StaleWatch, staleLabel } from "@helm/core";
import { useMarket, useMdStatus } from "./marketData";

/**
 * "No tick for 15 s while the market is open" (launch blocker CB3) for one symbol: the chart or
 * DOM ladder showing it may be frozen even though every socket looks connected. Nothing while the
 * market is closed, before sign-in (no live feed), or while the md socket is down (the widget
 * already says "disconnected").
 */
export function StaleBadge({
  symbol,
  onLabel,
  hidden = false,
}: {
  symbol: string | null;
  /** The short label ("stale 23 s") on change, null while live: the chart legend row shows it. */
  onLabel?: ((label: string | null) => void) | undefined;
  /** Someone else shows the label (the chart's legend row): render nothing here. */
  hidden?: boolean;
}): React.JSX.Element | null {
  // Keyed: a new symbol gets a fresh watch that counts from zero.
  return <SymbolStaleBadge key={symbol ?? ""} symbol={symbol} onLabel={onLabel} hidden={hidden} />;
}

function SymbolStaleBadge({
  symbol,
  onLabel,
  hidden,
}: {
  symbol: string | null;
  onLabel: ((label: string | null) => void) | undefined;
  hidden: boolean;
}): React.JSX.Element | null {
  const m = useMarket(symbol);
  const status = useMdStatus();
  const [stale, setStale] = useState(false);
  const watch = useMemo(() => new StaleWatch({ onChange: setStale }), []);
  useEffect(() => {
    watch.start();
    return () => {
      watch.stop();
    };
  }, [watch]);
  const last = m?.lastUpdateMs ?? null;
  useEffect(() => {
    if (last !== null) watch.touch(last);
  }, [watch, last]);
  // While stale, re-render every second so the age keeps counting.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!stale) return;
    const t = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(t);
    };
  }, [stale]);
  const shown = stale && m !== null && status === "connected";
  const age = shown ? staleLabel(last, m.watchSinceMs, Math.max(now, Date.now())) : null;
  const label = age === null ? null : `stale ${age.replace(/^No data for /, "")}`;
  useEffect(() => {
    onLabel?.(label);
  }, [onLabel, label]);
  if (!shown || age === null || hidden) return null;
  return (
    <span
      className="stale-badge"
      role="status"
      title={`${age} for ${m.info?.symbol ?? m.requested} while the market is open (nothing in ${String(STALE_AFTER_MS / 1000)} s). The feed may be frozen: check the T dot in the status bar.`}
    >
      Stale · {age.replace(/^No data for /, "")}
    </span>
  );
}
